import { LitElement, html, css, unsafeCSS, type TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "./web-kiosk-countdown-box";
import type { WhModalElement } from "@wh-web-front-end/wh-component-library/dist/elements/wh-modal";

import "@wh-web-front-end/wh-component-library/dist/elements/wh-button";

import "@wh-web-front-end/wh-component-library/dist/elements/wh-modal";
import type { EndSessionPromptContent } from "../types/content";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-end-session-prompt": WebKioskEndSessionPromptElement;
  }
}

@customElement("web-kiosk-end-session-prompt")
export class WebKioskEndSessionPromptElement extends LitElement {
  @state() content: EndSessionPromptContent | undefined = undefined;

  @query("wh-modal") modal: WhModalElement | undefined;

  openPrompt(): void {
    this.modal?.showModal();
  }

  closePrompt(): void {
    this.modal?.hideModal();
  }

  render(): TemplateResult {
    return html`
      <wh-modal flat fitContent .noHeader=${true}>
        <div class="modal-body">
          <h3 id="modal-title">${this.content?.modalTitle}</h3>
          <div class="buttons">
            <wh-button
              variant="secondary"
              id="leave-button"
              .fullwidth=${true}
              @click=${() => {
                this.dispatchEvent(
                  new CustomEvent("end-session", {
                    bubbles: true,
                    composed: true,
                  })
                );
              }}
            >
              ${this.content?.confirmText}
            </wh-button>
            <wh-button
              variant="primary"
              pill
              size="medium"
              id="stay-button"
              fullwidth="true"
              @click=${() => {
                this.dispatchEvent(
                  new CustomEvent("resume-session", {
                    bubbles: true,
                    composed: true,
                  })
                );
              }}
            >
              ${this.content?.denyText}
            </wh-button>
          </div>
        </div>
      </wh-modal>
    `;
  }

  static styles = css`
    :host {
      --web-kiosk-p-color: #0a1414;
      --web-kiosk-text-color: #323264;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-button-text-color: #fff;
      --wh-modal-background-color: #f5f5f0;
      --web-kiosk-dimension-spacer-xxs: 8px;
      --web-kiosk-dimension-spacer-xs: 16px;
      --web-kiosk-dimension-spacer-m: 32px;
      --web-kiosk-dimension-spacer-xl: 64px;
      --web-kiosk-font-size-s: 24px;
      --web-kiosk-font-size-m: 32px;
      --web-kiosk-font-size-l: 48px;
    }

    .modal-body {
      display: flex;
      flex-direction: column;
      align-items: center;
      overflow-y: auto;
      border-bottom-right-radius: var(--web-kiosk-dimension-spacer-xs, 16px);
      border-bottom-left-radius: var(--web-kiosk-dimension-spacer-xs, 16px);
      padding: 0 64px 64px;
      /* stylelint-disable-next-line declaration-colon-space-after */
      background: ${unsafeCSS(`
        url("assets/timeout-modal/ribbon.svg") bottom no-repeat,
        var(--wh-modal-background-color, hotpink);
      `)};
      background-size: contain;
    }

    .buttons {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 48px;
    }

    #modal-title {
      color: var(--web-kiosk-text-color, hotpink);
      font-size: var(--web-kiosk-font-size-l, 48px);
      margin-top: 24px;
      margin-bottom: 24px;
    }

    wh-button {
      border-radius: 50px;
      font-weight: 600;
      font-size: var(--web-kiosk-font-size-s, 24px);
      line-height: 20px;
      border: 2px solid var(--web-kiosk-text-color, hotpink);
      width: 160px;
    }

    #stay-button {
      color: var(--web-kiosk-button-text-color, hotpink);
      background-color: var(--web-kiosk-text-color, hotpink);
    }

    #leave-button {
      color: var(--web-kiosk-text-color, hotpink);

      --wh-theme-colors-secondary: var(--wh-modal-background-color, hotpink);
      --wh-theme-colors-secondary-weak: var(
        --wh-modal-background-color,
        hotpink
      );
    }
  `;
}
